import { create } from 'zustand';

import type { FeatureFlags, FeatureFlagsReturn } from 'constants/featureFlags';
import { defaultFeatureFlags } from 'constants/featureFlags';
import config from 'utils/config';
import { ReadJSON } from 'utils/s3';

interface FeatureFlagActions {
    featureFlagsFetched: (data: FeatureFlagsReturn) => void;
    fetchFeatureFlags: () => void;
}

interface FeatureFlagState extends FeatureFlags {
    loaded: boolean;
    actions: FeatureFlagActions;
}

let fetching = false;

export const useFeatureFlagStore = create<FeatureFlagState>()((set, get) => ({
    ...defaultFeatureFlags,
    loaded: false,

    actions: {
        featureFlagsFetched: (data: FeatureFlagsReturn) => {
            const isProd = config.frontendEnv === 'production';
            set({
                blockNyZeroHash: data.block_new_yorkers_zero_hash,
                isCardPaymentEnabled: data.is_card_payment_enabled,
                interestRate: data.interest_rate,
                interestRateAmount: data.interest_rate_amount,
                marginInterestRateAmount:
                    (typeof data.margin_interest_rate_amount === 'string' && data.margin_interest_rate_amount.trim()) ||
                    data.interest_rate_amount,
                enableWithdrawalMfa: data.enable_withdrawal_mfa,
                enableNewMfaMethods: data.enable_new_mfa_methods,
                cryptoWithdrawEnabled: data.crypto_withdraw_enabled || !isProd,
                cryptoDepositEnabled: data.crypto_deposit_enabled || !isProd,
                globalParlaysEnabled: data.global_parlays_enabled,
                marketsToRemove: data.markets_to_remove || [],
                restrictedTradingCategories: data.restricted_trading_categories || [],
                attributionSurveyTitles: data.attribution_survey_titles || [],
                enableProps: !isProd || data.enable_props,
                braintreePaypalEnabled: data.braintree_paypal_enabled ?? defaultFeatureFlags.braintreePaypalEnabled,
                braintreeVenmoEnabled: data.braintree_venmo_enabled ?? defaultFeatureFlags.braintreeVenmoEnabled,
                worldCupPromoBanner: data.world_cup_promo_banner ?? defaultFeatureFlags.worldCupPromoBanner,
                // Absent means enabled: the field only has to exist in S3 to turn the pause OFF.
                coveredContentPauseEnabled:
                    data.covered_content_pause_enabled ?? defaultFeatureFlags.coveredContentPauseEnabled,
                cfbUndefeated: data.cfb_undefeated,
                propToProtection: data.prop_to_protection,
                protectionMessages: data.protection_messages,
                loaded: true,
            });
        },

        fetchFeatureFlags: () => {
            if (get().loaded || fetching) return;

            // The import-time bootstrap below also runs under vitest (jsdom has a
            // window); pin the same snapshot tests have always seen instead of
            // hitting the network from every test file that imports this module.
            // This repo's vitest runs with NODE_ENV=development, so key off VITEST
            // (unset in Next's client-side process.env shim, 'true' in test workers).
            if (process.env.VITEST) {
                set({
                    cryptoWithdrawEnabled: true,
                    cryptoDepositEnabled: true,
                    enableProps: true,
                    loaded: true,
                });
                return;
            }

            fetching = true;

            // Every frontendEnv fetches real flags (the CDN serves
            // `access-control-allow-origin: *`, so non-production origins are fine).
            // Non-production builds skipping the fetch left flags at their defaults,
            // which diverges from prod in ways that break flows outright — e.g.
            // `enableWithdrawalMfa` defaulting to false made previews submit
            // withdrawals with an empty MFA code, which the backend always rejects.
            const onError = () => {
                fetching = false;
                if (config.frontendEnv !== 'production') {
                    // Same non-production overrides `featureFlagsFetched` applies on a
                    // successful fetch, so a failed fetch doesn't disable crypto/props
                    // flows that are supposed to stay on for local testing.
                    set({
                        cryptoWithdrawEnabled: true,
                        cryptoDepositEnabled: true,
                        enableProps: true,
                        loaded: true,
                    });
                    return;
                }
                set({ loaded: true });
            };

            ReadJSON<FeatureFlagsReturn>(
                `${config.kalshiPublicDocsCloudfrontUrl}/app_casual/feature_flags.json.gz`,
                onError,
            ).then((data) => {
                fetching = false;
                if (data) {
                    get().actions.featureFlagsFetched(data);
                }
            });
        },
    },
}));

if (typeof window !== 'undefined') {
    useFeatureFlagStore.getState().actions.fetchFeatureFlags();
}
